import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, User } from 'lucide-react';

export default function LoginPage() {
  const { loginAdmin, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [adminForm, setAdminForm] = useState({ username: '', password: '' });
  const [otpForm, setOtpForm] = useState({ username: '', otp: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginAdmin(adminForm.username, adminForm.password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!otpForm.username || otpForm.username.trim().length < 2) {
      toast.error('Enter a valid username');
      return;
    }
    setLoading(true);
    try {
      await requestOtp(otpForm.username.trim().toLowerCase());
      setOtpSent(true);
      toast.success('OTP generated! Ask your Admin or Store Manager for the code.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate OTP');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOtp(otpForm.username.trim().toLowerCase(), otpForm.otp);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#050505' }}>
      {/* Brand Panel with molten gold effect */}
      <div className="hidden lg:flex lg:w-1/2 login-brand-panel">
        <div className="relative z-10 text-center">
          <img src="/yash-gold-logo.png" alt="The Yash Gold App" className="w-72 h-auto mx-auto mb-4 logo-shimmer logo-glow" />
          <h1 className="text-3xl font-bold mb-1" style={{
            fontFamily: 'var(--font-heading)',
            background: 'linear-gradient(135deg, #F5DEB3, #FFD700, #DAA520, #CD7F32)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>The Yash Gold App</h1>
          <p className="text-sm opacity-50 max-w-xs" style={{ color: 'hsl(40, 25%, 75%)' }}>
            Complete stock management, sales tracking, and business insights for your gold wholesale operations.
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8" style={{ background: 'hsl(30, 6%, 8%)' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/yash-gold-logo.png" alt="The Yash Gold App" className="w-56 h-auto mx-auto mb-3 logo-glow" />
            <h1 className="text-2xl font-bold" style={{
              fontFamily: 'var(--font-heading)',
              background: 'linear-gradient(135deg, #F5DEB3, #FFD700, #DAA520, #CD7F32)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>The Yash Gold App</h1>
          </div>

          {/* Staff Login (Default) */}
          {!showAdminLogin ? (
            <div>
              <Card className="border-border/50" style={{ background: 'hsl(30, 8%, 11%)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><User size={20} style={{ color: 'hsl(43, 96%, 56%)' }} /> Staff Login</CardTitle>
                  <CardDescription>Enter your username to receive an OTP from admin</CardDescription>
                </CardHeader>
                <CardContent>
                  {!otpSent ? (
                    <form onSubmit={handleRequestOtp} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Username</label>
                        <Input
                          data-testid="login-staff-username-input"
                          value={otpForm.username}
                          onChange={(e) => setOtpForm({...otpForm, username: e.target.value})}
                          placeholder="Enter your username"
                          autoFocus
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={loading} data-testid="login-staff-otp-button">
                        {loading ? 'Requesting OTP...' : 'Request OTP'}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        Your OTP will appear on the Admin's dashboard
                      </p>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                      <div className="p-3 rounded-lg text-sm" style={{ background: 'hsl(43, 50%, 14%)', color: 'hsl(43, 80%, 65%)', border: '1px solid hsl(43, 50%, 25%)' }}>
                        OTP generated for <span className="font-mono font-semibold">{otpForm.username}</span>.
                        Ask your Admin or Store Manager for the code.
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Enter OTP</label>
                        <Input
                          data-testid="login-otp-input"
                          value={otpForm.otp}
                          onChange={(e) => setOtpForm({...otpForm, otp: e.target.value})}
                          placeholder="6-digit OTP"
                          maxLength={6}
                          className="text-center text-lg font-mono tracking-widest"
                          autoFocus
                        />
                      </div>
                      <Button data-testid="login-otp-submit-button" type="submit" className="w-full" disabled={loading}>
                        {loading ? 'Verifying...' : 'Verify & Login'}
                      </Button>
                      <Button variant="ghost" className="w-full" onClick={() => setOtpSent(false)}>
                        Change Username
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowAdminLogin(true)}
                  className="text-xs font-medium transition-colors duration-150 hover:underline"
                  style={{ color: 'hsl(43, 60%, 50%)' }}
                  data-testid="switch-to-admin-login"
                >
                  <ShieldCheck size={12} className="inline mr-1" />
                  Admin Login
                </button>
              </div>
            </div>
          ) : (
            /* Admin Login */
            <div>
              <Card className="border-border/50" style={{ background: 'hsl(30, 8%, 11%)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><ShieldCheck size={20} style={{ color: 'hsl(43, 96%, 56%)' }} /> Admin Login</CardTitle>
                  <CardDescription>Enter your username and password</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAdminLogin} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Username</label>
                      <Input
                        data-testid="login-admin-username-input"
                        value={adminForm.username}
                        onChange={(e) => setAdminForm({...adminForm, username: e.target.value})}
                        placeholder="Enter username"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Password</label>
                      <Input
                        data-testid="login-admin-password-input"
                        type="password"
                        value={adminForm.password}
                        onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                        placeholder="Enter password"
                      />
                    </div>
                    <Button data-testid="login-admin-submit-button" type="submit" className="w-full" disabled={loading}>
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowAdminLogin(false)}
                  className="text-xs font-medium transition-colors duration-150 hover:underline"
                  style={{ color: 'hsl(43, 60%, 50%)' }}
                  data-testid="switch-to-staff-login"
                >
                  <User size={12} className="inline mr-1" />
                  Back to Staff Login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
